<script lang="ts">
  import { StepperOrientation, StepperTheme } from './Stepper.types';
  export * from './Stepper.types';

  export interface StepperProps {
    /**
     * The current step index
     */
    step?: number;
    /**
     * The current substep index
     */
    substep?: number;
    /**
     * A linear stepper prevents navigation to steps that are ahead of the completed ones
     */
    linear?: boolean;
    /**
     * Determines whether the stepper is horizontal or vertical
     */
    orientation?: StepperOrientation;
    /**
     * Force responsive view on horizontal orientation
     */
    useResponsiveNav?: boolean;
    /**
     * Determines the theme of the stepper
     */
    theme?: StepperTheme;
  }
</script>

<script setup lang="ts">
  import { computed, nextTick, provide, ref, watch, watchEffect } from 'vue';

  import useMediaQuery from '../../composables/useMediaQuery/useMediaQuery';
  import useStepper from '../../composables/useStepper/useStepper';
  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';
  import { STEPPER_INJECTION_KEY } from './keys';

  const props = withDefaults(defineProps<StepperProps>(), {
    step: -1,
    substep: -1,
    linear: false,
    orientation: 'vertical',
    theme: 'dark',
    useResponsiveNav: false,
  });

  const emit = defineEmits<{
    /**
     * Emitted when the step changes
     */
    (e: 'update:step', step: number): void;
    /**
     * Emitted when the substep changes
     */
    (e: 'update:substep', substep: number): void;
  }>();

  const stepperListRef = ref();
  const stepper = useStepper({
    linear: props.linear,
    activeStep: props.step,
    activeSubstep: props.substep,
    ref: stepperListRef,
  });

  const isLargeScreen = useMediaQuery('screen and (min-width: 961px)');
  const showResponsiveNav = computed(
    () => props.orientation === 'horizontal' && (!isLargeScreen.value || props.useResponsiveNav),
  );
  const isPrevArrowDisabled = computed(() => stepper.activeStepIndex.value <= 0);
  const isNextArrowDisabled = computed(
    () =>
      !stepper.isStepCompleted(stepper.activeStepIndex.value) ||
      stepper.activeStepIndex.value === stepper.steps.value.length - 1,
  );

  watch(
    () => props.step,
    (value) => (stepper.activeStepIndex.value = value),
  );

  watch(
    () => props.substep,
    (value) => (stepper.activeSubstepIndex.value = value),
  );

  watch(
    () => stepper.activeStepIndex.value,
    (value) => {
      emit('update:step', value);
      scrollToStep();
    },
  );

  watch(
    () => stepper.activeSubstepIndex.value,
    (value) => emit('update:substep', value),
  );

  /**
   * Watches for media query changes to determine if the mobile horizontal stepper is active.
   * This is done to ensure the mobile stepper remains in sync with any step changes at the desktop view.
   */
  watchEffect(() => {
    if (showResponsiveNav.value) {
      scrollToStep();
    }
  });

  provide(STEPPER_INJECTION_KEY, {
    registerStep: stepper.registerStep,
    orientation: props.orientation,
    stepCount: stepper.stepCount,
    theme: props.theme,
    showResponsiveNav,
  });

  defineExpose({
    back: stepper.back,
    next: stepper.next,
    goTo: stepper.goTo,
    isStepCompleted: stepper.isStepCompleted,
    isStepActive: stepper.isStepActive,
  });

  /**
   * Scrolls to the active step when the horizontal stepper is active.
   * This limited to top-level steps only.
   */
  async function scrollToStep() {
    if (!showResponsiveNav.value) {
      return;
    }

    // allows the DOM to update before scrolling to the active step.
    await nextTick();

    const step = stepper.activeStepElement.value;

    if (step) {
      step.scrollIntoView({ behavior: 'instant', inline: 'start', block: 'nearest' });
    }
  }
</script>

<template>
  <div class="relative">
    <ul
      ref="stepperListRef"
      class="stash-stepper flex"
      :class="{
        'flex-col': props.orientation === 'vertical',
        'stash-stepper--vertical': props.orientation === 'vertical',
        'stash-stepper--horizontal': props.orientation === 'horizontal',
        'mx-9 snap-x snap-mandatory overflow-x-hidden': showResponsiveNav,
      }"
      data-test="stash-stepper"
    >
      <!-- @slot Default slot to compose multiple steps -->
      <slot></slot>
    </ul>
    <Button
      v-if="showResponsiveNav"
      class="stash-stepper__arrow stash-stepper__arrow--prev"
      :class="props.theme === 'light' ? 'text-blue-500' : 'text-white'"
      data-test="stash-stepper|prev-arrow"
      :disabled="isPrevArrowDisabled"
      icon
      @click="stepper.back"
    >
      <Icon name="chevron-left" size="dense" />
    </Button>
    <Button
      v-if="showResponsiveNav"
      class="stash-stepper__arrow stash-stepper__arrow--next"
      :class="props.theme === 'light' ? 'text-blue-500' : 'text-white'"
      data-test="stash-stepper|next-arrow"
      :disabled="isNextArrowDisabled"
      icon
      @click="stepper.next"
    >
      <Icon name="chevron-right" size="dense" />
    </Button>
  </div>
</template>

<style>
  @reference "../../../styles/main.css";

  @layer utilities {
    .stash-stepper--vertical .stash-step:last-child .stash-step--nested-steps {
      @apply hidden;
    }

    .stash-stepper__arrow {
      @apply absolute
        top-0
        flex
        h-6
        w-6
        min-w-auto
        items-center
        justify-center
        rounded-full
        border
        border-solid
        border-ice-500
        text-center
        disabled:text-ice-500 disabled:opacity-50;
    }

    .stash-stepper__arrow--prev {
      @apply left-0;
    }

    .stash-stepper__arrow--next {
      @apply right-0;
    }
  }
</style>
